import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { LoadsForAllProductsService } from '../../root/loads-for-all-products.service';
import { StatementService } from '../../settlement-statement/statement.service';
import { PaymentIssue, SettlementStatementPaymentIssueType } from '../../types';

export type PaymentIssueDialogMode = 'report' | 'submitted' | 'resolved';
export type PaymentIssueDialogContext = 'settlement' | 'load';

export interface PaymentIssueDialogData {
  mode: PaymentIssueDialogMode;
  context: PaymentIssueDialogContext;
  settlementId?: number;
  loadId?: string;
  paymentIssue?: PaymentIssue | null;
  link: { routerLink: any[]; text: string };
}

@Component({
  selector: 'lohi-payment-issue-dialog',
  templateUrl: './payment-issue-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentIssueDialogComponent {
  public issueTypes$: Observable<SettlementStatementPaymentIssueType[]>;
  public selectedIssueTypeIds = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PaymentIssueDialogData,
    private dialogRef: MatDialogRef<PaymentIssueDialogComponent>,
    @Optional() private statementService: StatementService,
    @Optional() private loadService: LoadsForAllProductsService,
    private router: Router,
  ) {
    // Load issue types based on context
    if (this.data.context === 'load') {
      this.issueTypes$ = this.loadService.getLoadIssueTypes$();
    } else {
      this.issueTypes$ = this.statementService.getIssueTypes$();
    }
  }

  public getTargetResolveDate(): Date | null {
    const createdAt = this.data?.paymentIssue?.createdAt;
    if (!createdAt) {
      return null;
    }
    const created = new Date(createdAt);
    const msIn48Hours = 48 * 60 * 60 * 1000;
    return new Date(created.getTime() + msIn48Hours);
  }

  public toggleIssueType(issueTypeId: string): void {
    if (this.selectedIssueTypeIds.has(issueTypeId)) {
      this.selectedIssueTypeIds.delete(issueTypeId);
    } else {
      this.selectedIssueTypeIds.add(issueTypeId);
    }
  }

  public isIssueTypeSelected(issueTypeId: string): boolean {
    return this.selectedIssueTypeIds.has(issueTypeId);
  }

  public getIssueTypeDisplayNames(issueTypes: SettlementStatementPaymentIssueType[]): string[] {
    const issueTypeIds = this.data.paymentIssue?.issueTypeIds || [];
    const typeMap = new Map(issueTypes.map((type) => [type.id, type.displayName]));
    return issueTypeIds.map((id) => typeMap.get(id)).filter((name): name is string => !!name);
  }

  public hasIssueTypes(): boolean {
    return (this.data.paymentIssue?.issueTypeIds?.length || 0) > 0;
  }

  public trackByIssueTypeId(index: number, issueType: SettlementStatementPaymentIssueType): string {
    return issueType.id;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public async submit(): Promise<void> {
    if (this.data.mode !== 'report') {
      this.dialogRef.close();
      return;
    }
    if (this.selectedIssueTypeIds.size === 0) {
      return;
    }

    try {
      if (this.data.context === 'load' && this.data.loadId) {
        await this.loadService
          .reportLoadPaymentIssue$(this.data.loadId, Array.from(this.selectedIssueTypeIds))
          .toPromise();
        this.loadService.refreshCompletedLoads();
      } else if (this.data.context === 'settlement' && this.data.settlementId) {
        await this.statementService
          .reportPaymentIssue$(this.data.settlementId, Array.from(this.selectedIssueTypeIds))
          .toPromise();
        this.statementService.refreshSummaries();
      } else {
        this.dialogRef.close({ submitted: false });
        return;
      }
      this.dialogRef.close({ submitted: true });
    } catch {
      this.dialogRef.close({ submitted: false });
    }
  }

  public goToLink(): void {
    const link = this.data?.link?.routerLink;
    if (Array.isArray(link)) {
      this.router.navigate(link);
    }
    this.dialogRef.close();
  }
}
